import React, { useEffect, useState } from "react";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import { getCodeList } from "country-list";
import apiUser from "app/api/user";
import { Button, DatePickerInput, FormInput, Forms, FormSelect, notify } from "@housebets/housebets-x-package";

import { IBaseVerificationFormProps, TFormikConfig, TPayload } from "./types";
import dayjs from "dayjs";

const countryList = getCodeList();

const countryOptions = Object.keys(countryList).map((item) => ({
    name: countryList[item],
    id: item.toUpperCase(),
}));

const initData: TFormikConfig = {
    firstName: "",
    lastName: "",
    birth: null,
    address: "",
    country: countryOptions[0].id as string,
};

const initForm = (formik: FormikProps<TFormikConfig>, setError?: (v: boolean) => void) => {
    formik.setValues({ ...initData });
    formik.setErrors({});
    formik.setTouched({});
    setError?.(false);
};

const formSchema = () => {
    return Yup.object().shape({
        firstName: Yup.string().required("Field is required"),
        lastName: Yup.string().required("Field is required"),
        birth: Yup.string().required("Field is required"),
        address: Yup.string().required("Field is required"),
        country: Yup.string().required("Field is required"),
    });
};

export const BaseVerificationForm: React.FC<IBaseVerificationFormProps> = ({
    onSubmit = () => { }
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: { ...initData },
        validationSchema: formSchema(),
        validateOnBlur: true,
        onSubmit: async (data: TPayload) => {
            try {
                setIsLoading(true);

                const {
                    country,
                    birth,
                    firstName,
                    lastName,
                    address,
                } = data;

                const _payLoad = {
                    address: address,
                    birth: dayjs(birth).format('YYYY-MM-DD'),
                    city: "",
                    country: country,
                    firstName: firstName,
                    gender: "0",
                    lastName: lastName,
                    zipCode: "0",
                };
                const res = await apiUser.updateUserDetail(_payLoad);

                if (res.status === 200 && res.data.code === 0) {
                    notify({ type: "success", title: `Basic verification activated` });
                    initForm(formik);
                    onSubmit();
                } else {
                    setError("some error");
                }
            } catch (er) {
                setError("some error");
            } finally {
                setIsLoading(false);
            }
        },
    });

    useEffect(() => {
        initForm(formik);
    }, []);

    return (
        <Forms.BasicVerify.Wrapper>
            <Forms.BasicVerify.Form
                onSubmit={formik.handleSubmit}
            >
                <Forms.BasicVerify.Row>
                    <FormSelect
                        errorMsgAbsolute
                        size="sm"
                        options={countryOptions}
                        placeholder="Select value"
                        label="Country"
                        required
                        name="country"
                        value={formik.values?.country as string}
                        errors={
                            (!!formik.errors?.country && !!formik.touched?.country) || error
                                ? {
                                    message: formik.errors?.country || error,
                                }
                                : null
                        }
                        onChange={(val) => formik.setFieldValue("country", val[0])}
                        onFocus={() => {
                            formik.setFieldTouched("country", false);
                        }}
                        onBlur={() => {
                            formik.setFieldTouched("country", true);
                        }}
                    />
                </Forms.BasicVerify.Row>
                <Forms.BasicVerify.Row>
                    <FormInput
                        errorMsgAbsolute
                        showIcon={false}
                        size="sm"
                        placeholder="First name"
                        name="firstName"
                        type="text"
                        required
                        label="First name"
                        value={formik.values?.firstName}
                        errors={
                            (!!formik.errors?.firstName && !!formik.touched?.firstName) || error
                                ? {
                                    message: formik.errors?.firstName || error,
                                }
                                : null
                        }
                        onChange={(val) => formik.setFieldValue("firstName", val)}
                        onFocus={() => formik.setFieldTouched("firstName", false)}
                        onBlur={() => formik.setFieldTouched("firstName", true)}

                    />
                </Forms.BasicVerify.Row>
                <Forms.BasicVerify.Row>
                    <FormInput
                        errorMsgAbsolute
                        showIcon={false}
                        size="sm"
                        placeholder="Last name"
                        name="lastName"
                        type="text"
                        label="Last name"
                        required
                        value={formik.values?.lastName}
                        errors={
                            (!!formik.errors?.lastName && !!formik.touched?.lastName) || error
                                ? {
                                    message: formik.errors?.lastName || error,
                                }
                                : null
                        }
                        onChange={(val) => formik.setFieldValue("lastName", val)}
                        onFocus={() => formik.setFieldTouched("lastName", false)}
                        onBlur={() => formik.setFieldTouched("lastName", true)}

                    />
                </Forms.BasicVerify.Row>
                <Forms.BasicVerify.Row>
                    <DatePickerInput
                        placeholder="Select date"
                        id="birth"
                        name="birth"
                        label="Date of birthday"
                        errorMsgAbsolute
                        required
                        size="sm"
                        fullWidth
                        selected={formik.values?.birth}
                        errors={
                            (!!formik.errors?.birth && !!formik.touched?.birth) || error
                                ? {
                                    message: formik.errors?.birth || error || '',
                                }
                                : null
                        }
                        maxDate={new Date()}
                        onChange={(val) => formik.setFieldValue("birth", val)}
                        onFocus={() => formik.setFieldTouched("birth", false)}
                        onBlur={() => formik.setFieldTouched("birth", true)}
                    />
                </Forms.BasicVerify.Row>
                <Forms.BasicVerify.Row>
                    <FormInput
                        showIcon={false}
                        errorMsgAbsolute
                        size="sm"
                        placeholder="Address"
                        name="address"
                        required
                        type="text"
                        label="Address"
                        value={formik.values?.address}
                        errors={
                            (!!formik.errors?.address && !!formik.touched?.address) || error
                                ? {
                                    message: formik.errors?.address || error,
                                }
                                : null
                        }
                        onChange={(val) => formik.setFieldValue("address", val)}
                        onFocus={() => formik.setFieldTouched("address", false)}
                        onBlur={() => formik.setFieldTouched("address", true)}
                    />
                </Forms.BasicVerify.Row>
                <Forms.BasicVerify.Row>
                    <Button
                        text="Submit"
                        themeKind="blue"
                        size="medium-3"
                        type="submit"
                        disabled={!formik.isValid || isLoading}
                    />
                </Forms.BasicVerify.Row>
            </Forms.BasicVerify.Form>
        </Forms.BasicVerify.Wrapper>
    )
}

BaseVerificationForm.displayName = 'BaseVerificationForm'